import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Home, Search, Bell, User, LogIn, Bookmark, LogOut, Settings } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import NotificationFeed from '@/components/layout/NotificationFeed';

const tabs = [
    { name: 'Home', icon: Home, path: '/' },
    { name: 'Search', icon: Search, path: '/search' },
];

// Notifications (in-app alerts) require a paid tier. Per docs/tier-permissions.md,
// Hermes and Apollo have alerts access; Atrium (and logged-out) do not.
const NOTIFICATION_TIERS = ['HERMES', 'APOLLO'];
const UPGRADE_MESSAGE = 'Upgrade to Hermes or Apollo to get notifications.';

export default function MobileBottomBar() {
    const { isLoggedIn, user, logout } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [notificationsOpen, setNotificationsOpen] = useState(false);
    const [accountOpen, setAccountOpen] = useState(false);

    const hasNotificationAccess =
        isLoggedIn && NOTIFICATION_TIERS.includes((user?.membershipTier ?? '').toUpperCase());

    const { data: unreadData } = useQuery({
        queryKey: queryKeys.notificationUnreadCount,
        queryFn: () => api.getUnreadCount(),
        enabled: hasNotificationAccess,
        refetchInterval: 60_000,
    });

    const isActive = (path: string) =>
        path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

    const unreadCount = unreadData?.count ?? 0;
    const badgeLabel = unreadCount > 9 ? '9+' : String(unreadCount);
    const notificationsActive = notificationsOpen;
    const accountActive = accountOpen || isActive('/dashboard') || isActive('/settings');
    const notificationsClass = `flex-1 flex flex-col items-center justify-center gap-1 transition-colors ${
        notificationsActive ? 'text-pantheon-teal' : 'text-white/40 hover:text-white/70'
    }`;

    const closeAccountMenu = () => setAccountOpen(false);

    const handleLogout = () => {
        closeAccountMenu();
        logout();
        navigate('/');
    };

    return (
        <>
            {hasNotificationAccess && notificationsOpen && (
                <>
                    <button
                        type="button"
                        aria-label="Close notifications"
                        onClick={() => setNotificationsOpen(false)}
                        className="lg:hidden fixed inset-0 z-[205] bg-navy-950/50"
                    />
                    <div
                        role="dialog"
                        aria-label="Notifications"
                        className="lg:hidden fixed inset-x-3 bottom-20 z-[210] rounded-xl border border-white/10 bg-navy-900 p-2 text-white shadow-2xl"
                    >
                        <NotificationFeed
                            isOpen={notificationsOpen}
                            membershipTier={user?.membershipTier}
                            onClose={() => setNotificationsOpen(false)}
                        />
                    </div>
                </>
            )}
            {isLoggedIn && accountOpen && (
                <>
                    <button
                        type="button"
                        aria-label="Close account menu"
                        onClick={closeAccountMenu}
                        className="lg:hidden fixed inset-0 z-[205] bg-navy-950/50"
                    />
                    <div
                        role="dialog"
                        aria-label="Account menu"
                        className="lg:hidden fixed inset-x-3 bottom-20 z-[210] rounded-xl border border-white/10 bg-navy-900 p-3 text-white shadow-2xl"
                    >
                        <div className="px-2 pb-2 text-[10px] font-black uppercase tracking-[0.2em] text-white/40">
                            Account
                        </div>
                        <Link
                            to="/dashboard"
                            onClick={closeAccountMenu}
                            className="mb-1 flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left text-white/80 transition-colors hover:bg-white/5 hover:text-white"
                        >
                            <span className="grid h-8 w-8 flex-shrink-0 place-items-center rounded-lg bg-white/5 text-white/50">
                                <Home className="h-4 w-4" />
                            </span>
                            <span className="min-w-0 flex-1 truncate text-sm font-bold tracking-wide">
                                Dashboard
                            </span>
                        </Link>
                        <Link
                            to="/settings"
                            onClick={closeAccountMenu}
                            className="mb-1 flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left text-white/80 transition-colors hover:bg-white/5 hover:text-white"
                        >
                            <span className="grid h-8 w-8 flex-shrink-0 place-items-center rounded-lg bg-white/5 text-white/50">
                                <Settings className="h-4 w-4" />
                            </span>
                            <span className="min-w-0 flex-1 truncate text-sm font-bold tracking-wide">
                                Profile Settings
                            </span>
                        </Link>
                        <div className="my-2 h-px bg-white/5" />
                        <button
                            type="button"
                            onClick={handleLogout}
                            className="flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left text-red-400 transition-colors hover:bg-red-500/10"
                        >
                            <span className="grid h-8 w-8 flex-shrink-0 place-items-center rounded-lg bg-red-500/10 text-red-400">
                                <LogOut className="h-4 w-4" />
                            </span>
                            <span className="min-w-0 flex-1 truncate text-sm font-bold tracking-wide">
                                Logout
                            </span>
                        </button>
                    </div>
                </>
            )}
            <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-[200] bg-navy-900 border-t border-white/10 safe-area-inset-bottom">
                <div className="flex items-stretch h-16">
                    {tabs.map(({ name, icon: Icon, path }) => (
                        <Link
                            key={path}
                            to={path}
                            className={`flex-1 flex flex-col items-center justify-center gap-1 transition-colors ${
                                isActive(path)
                                    ? 'text-pantheon-teal'
                                    : 'text-white/40 hover:text-white/70'
                            }`}
                        >
                            <Icon className="w-5 h-5" />
                            <span className="text-[9px] font-bold tracking-widest uppercase">{name}</span>
                            {isActive(path) && (
                                <span className="absolute bottom-0 w-6 h-0.5 bg-pantheon-teal rounded-full" />
                            )}
                        </Link>
                    ))}

                    {/* Notifications open the same in-app feed surface as the desktop bell.
                        Saved Flights is the create/manage surface for Price & Transfer Alerts. */}
                    {hasNotificationAccess ? (
                        <button
                            type="button"
                            onClick={() => {
                                setAccountOpen(false);
                                setNotificationsOpen((open) => !open);
                            }}
                            aria-label="Notifications"
                            aria-haspopup="dialog"
                            aria-expanded={notificationsOpen}
                            className={notificationsClass}
                        >
                            <span className="relative">
                                <Bell className="w-5 h-5" />
                                {unreadCount > 0 && (
                                    <span className="absolute -top-2 -right-2 min-w-[16px] h-4 bg-pantheon-teal rounded-full border-2 border-navy-900 flex items-center justify-center text-[8px] font-black text-white px-0.5">
                                        {badgeLabel}
                                    </span>
                                )}
                            </span>
                            <span className="text-[9px] font-bold tracking-widest uppercase">Notifications</span>
                            {notificationsActive && (
                                <span className="absolute bottom-0 w-6 h-0.5 bg-pantheon-teal rounded-full" />
                            )}
                        </button>
                    ) : (
                        <button
                            type="button"
                            onClick={() => toast(UPGRADE_MESSAGE)}
                            aria-label={`Notifications — ${UPGRADE_MESSAGE}`}
                            className={notificationsClass}
                        >
                            <Bell className="w-5 h-5" />
                            <span className="text-[9px] font-bold tracking-widest uppercase">Notifications</span>
                        </button>
                    )}

                    {/* Saved Flights — create/manage surface for Price & Transfer Alerts.
                        Lives next to Account in the bottom banner (moved out of the side drawer). */}
                    <Link
                        to="/saved-flights"
                        className={`flex-1 flex flex-col items-center justify-center gap-1 transition-colors ${
                            isActive('/saved-flights')
                                ? 'text-pantheon-teal'
                                : 'text-white/40 hover:text-white/70'
                        }`}
                    >
                        <Bookmark className="w-5 h-5" />
                        <span className="text-[9px] font-bold tracking-widest uppercase">Saved</span>
                        {isActive('/saved-flights') && (
                            <span className="absolute bottom-0 w-6 h-0.5 bg-pantheon-teal rounded-full" />
                        )}
                    </Link>

                    {/* Profile tab */}
                    {isLoggedIn ? (
                        <button
                            type="button"
                            onClick={() => {
                                setNotificationsOpen(false);
                                setAccountOpen((open) => !open);
                            }}
                            aria-label="Account"
                            aria-haspopup="dialog"
                            aria-expanded={accountOpen}
                            className={`flex-1 flex flex-col items-center justify-center gap-1 transition-colors ${
                                accountActive
                                    ? 'text-pantheon-teal'
                                    : 'text-white/40 hover:text-white/70'
                            }`}
                        >
                            <User className="w-5 h-5" />
                            <span className="text-[9px] font-bold tracking-widest uppercase">
                                Account
                            </span>
                            {accountActive && (
                                <span className="absolute bottom-0 w-6 h-0.5 bg-pantheon-teal rounded-full" />
                            )}
                        </button>
                    ) : (
                        <Link
                            to="/auth?mode=signin"
                            className="flex-1 flex flex-col items-center justify-center gap-1 transition-colors text-white/40 hover:text-white/70"
                        >
                            <LogIn className="w-5 h-5" />
                            <span className="text-[9px] font-bold tracking-widest uppercase">
                                Sign In
                            </span>
                        </Link>
                    )}
                </div>
            </nav>
        </>
    );
}
